import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getSeedReports, getAllAnalyses } from '../utils/api';
import { FileText, Search, ChevronRight, Loader, RefreshCw, Bot } from 'lucide-react';
import './ReportsPage.css';

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const [seedRes, userRes] = await Promise.allSettled([
        getSeedReports(),
        getAllAnalyses()
      ]);
      const seed = seedRes.status === 'fulfilled' ? seedRes.value.data || [] : [];
      const user = userRes.status === 'fulfilled' ? userRes.value.data || [] : [];
      // User reports first, then seed
      const all = [...user, ...seed].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      setReports(all);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const filtered = reports.filter(r => {
    if (!search) return true;
    return `${r.homeTeam} ${r.awayTeam} ${r.competition}`.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="reports-page fade-in">
      <div className="reports-page__header">
        <div>
          <h1>Match Reports</h1>
          <p>{reports.length} total · {reports.filter(r => r.aiGenerated).length} AI generated</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={loadReports}>
            <RefreshCw size={14} /> Refresh
          </button>
          <Link to="/upload" className="btn btn-primary">+ New Analysis</Link>
        </div>
      </div>

      <div className="reports-search">
        <Search size={16} />
        <input
          placeholder="Search by team or competition..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="reports-loading">
          <Loader size={24} className="spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : (
        <div className="reports-grid stagger">
          {filtered.map(r => (
            <Link key={r.id} to={`/reports/${r.id}`} className="report-card card card-hover">
              <div className="report-card__top">
                <span className={`badge badge-${r.status === 'complete' ? 'green' : r.status === 'processing' ? 'amber' : 'red'}`}>
                  {r.status}
                </span>
                {r.competition && <span className="badge badge-blue">{r.competition}</span>}
                {r.aiGenerated && (
                  <span className="badge badge-purple" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Bot size={10} /> AI
                  </span>
                )}
              </div>

              <div className="report-card__match">
                <div className="report-card__team">
                  <div className="team-chip">{r.homeTeam?.[0]}</div>
                  <span>{r.homeTeam}</span>
                </div>
                {r.report?.score && (
                  <div className="report-card__score">
                    {r.report.score.home} – {r.report.score.away}
                  </div>
                )}
                <div className="report-card__team report-card__team--right">
                  <span>{r.awayTeam}</span>
                  <div className="team-chip">{r.awayTeam?.[0]}</div>
                </div>
              </div>

              {r.report?.formations && (
                <div className="report-card__formations">
                  <span>{r.report.formations.home?.name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>vs</span>
                  <span>{r.report.formations.away?.name}</span>
                </div>
              )}

              <div className="report-card__footer">
                <span className="report-card__date">{r.matchDate}</span>
                <span className="report-card__cta">View Report <ChevronRight size={13} /></span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="reports-empty">
          <FileText size={40} style={{ color: 'var(--text-muted)', margin: '0 auto 12px', display: 'block' }} />
          <p>{search ? 'No reports match your search' : 'No match reports yet'}</p>
          {!search && (
            <Link to="/upload" className="btn btn-primary" style={{ marginTop: 16 }}>
              Upload your first match
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
